<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  UiAside,
  UiButton,
  UiCard,
  UiChip,
  UiDialog,
  UiDivider,
  UiIconButton,
  UiInfoBox,
  UiText,
  UiTooltip,
} from "@shopexperts/shared";
import type { ExpertPlan, PlanBillingOption } from "@shopexperts/legacy-api";
import PageHeader from "@/app/components/common/PageHeader.vue";
import PlanRail from "@/app/components/expert-subscriptions/PlanRail.vue";
import PlanForm from "@/app/components/expert-subscriptions/PlanForm.vue";
import BillingOptionForm from "@/app/components/expert-subscriptions/BillingOptionForm.vue";
import PricingTable from "@/app/components/expert-subscriptions/PricingTable.vue";
import { useAdminExpertPlans } from "@/app/composables/expert-plans/useAdminExpertPlans";
import { useCreateExpertPlan } from "@/app/composables/expert-plans/useCreateExpertPlan";
import { useUpdateExpertPlan } from "@/app/composables/expert-plans/useUpdateExpertPlan";
import { useDeleteExpertPlan } from "@/app/composables/expert-plans/useDeleteExpertPlan";
import {
  defaultExpertPlanFormValues,
  describeExpertPlanType,
  expertPlanToFormValues,
  formatExpertPlanType,
  formValuesToCreatePayload,
  formValuesToUpdatePayload,
  type ExpertPlanFormValues,
} from "@/app/composables/expert-plans/expertPlanForm";
import { buildDeletePlanCopy } from "@/app/composables/expert-plans/deletePlanCopy";
import { useAdminPlanBillingOptions } from "@/app/composables/expert-billing/useAdminPlanBillingOptions";
import { useCreateBillingOption } from "@/app/composables/expert-billing/useCreateBillingOption";
import { useUpdateBillingOption } from "@/app/composables/expert-billing/useUpdateBillingOption";
import { useDeleteBillingOption } from "@/app/composables/expert-billing/useDeleteBillingOption";
import {
  billingOptionToFormValues,
  defaultBillingOptionFormValues,
  formValuesToCreateBillingOptionPayload,
  formValuesToUpdateBillingOptionPayload,
  formatOptionAmount,
  extractBillingValidationErrors,
  type BillingOptionFormValues,
} from "@/app/composables/expert-billing/billingOptionForm";

const { plans, isLoading } = useAdminExpertPlans();

const selectedPlanId = ref<number | null>(null);
const selectedPlan = computed(() => plans.value.find((plan) => plan.id === selectedPlanId.value) ?? null);

// Keep a valid selection as plans load or the selected plan is deleted.
watch(plans, (next) => {
  if (next.length === 0) {
    selectedPlanId.value = null;
    return;
  }
  if (!next.some((plan) => plan.id === selectedPlanId.value)) {
    selectedPlanId.value = next[0]?.id ?? null;
  }
}, { immediate: true });

const planAsideOpen = ref(false);
const optionAsideOpen = ref(false);
const editingOption = ref<PlanBillingOption | null>(null);
const planFormRevision = ref(0);
const planSubmissionInFlight = ref(false);
const createPlanServerErrors = ref<Record<string, string> | undefined>();
const updatePlanServerErrors = ref<Record<string, string> | undefined>();

const { options, isLoading: optionsLoading } = useAdminPlanBillingOptions(selectedPlanId);

const { mutateAsync: createPlan, isPending: isCreatingPlan } = useCreateExpertPlan();
const { mutateAsync: updatePlan, isPending: isUpdatingPlan } = useUpdateExpertPlan();
const { mutateAsync: deletePlan, isPending: isDeletingPlan } = useDeleteExpertPlan();
const { mutateAsync: createOption, isPending: isCreatingOption } = useCreateBillingOption();
const { mutateAsync: updateOption, isPending: isUpdatingOption } = useUpdateBillingOption();
const { mutateAsync: deleteOption, isPending: isDeletingOption } = useDeleteBillingOption();

const isSavingPlan = computed(
  () => planSubmissionInFlight.value || isCreatingPlan.value || isUpdatingPlan.value,
);
const isSavingOption = computed(() => isCreatingOption.value || isUpdatingOption.value);

const selectedPlanFormInitialValues = computed<ExpertPlanFormValues>(() =>
  selectedPlan.value
    ? expertPlanToFormValues(selectedPlan.value)
    : defaultExpertPlanFormValues(),
);

const optionFormInitialValues = computed<BillingOptionFormValues>(() =>
  editingOption.value
    ? billingOptionToFormValues(editingOption.value)
    : defaultBillingOptionFormValues(),
);

const subsLabel = computed(() => {
  const count = selectedPlan.value?.subscriptions_count ?? 0;
  if (count === 0) return "No subscriptions yet";
  if (count === 1) return "1 subscription";
  return `${count} subscriptions`;
});

function selectPlan(plan: ExpertPlan) {
  selectedPlanId.value = plan.id;
  updatePlanServerErrors.value = undefined;
  optionAsideOpen.value = false;
  editingOption.value = null;
}

function openCreatePlan() {
  createPlanServerErrors.value = undefined;
  planAsideOpen.value = true;
}

function closePlanAside() {
  if (isSavingPlan.value) return;
  createPlanServerErrors.value = undefined;
  planAsideOpen.value = false;
}

function openOptionAside(option: PlanBillingOption | null = null) {
  editingOption.value = option;
  optionAsideOpen.value = true;
}

function closeOptionAside() {
  if (isSavingOption.value) return;
  optionAsideOpen.value = false;
  editingOption.value = null;
}

async function onCreatePlanSubmit(values: ExpertPlanFormValues) {
  if (planSubmissionInFlight.value) return;
  planSubmissionInFlight.value = true;
  createPlanServerErrors.value = undefined;
  try {
    const created = await createPlan(formValuesToCreatePayload(values));
    if (created?.id) selectedPlanId.value = created.id;
    planAsideOpen.value = false;
  } catch (err) {
    const validationErrors = extractBillingValidationErrors(err);
    createPlanServerErrors.value = Object.keys(validationErrors).length > 0 ? validationErrors : undefined;
    console.error("[AdminExpertSubscriptions] Failed to save plan:", err);
  } finally {
    planSubmissionInFlight.value = false;
  }
}

async function onUpdatePlanSubmit(values: ExpertPlanFormValues) {
  if (!selectedPlan.value || planSubmissionInFlight.value) return;
  planSubmissionInFlight.value = true;
  updatePlanServerErrors.value = undefined;
  try {
    await updatePlan({ planId: selectedPlan.value.id, data: formValuesToUpdatePayload(values) });
    planFormRevision.value += 1;
  } catch (err) {
    const validationErrors = extractBillingValidationErrors(err);
    updatePlanServerErrors.value = Object.keys(validationErrors).length > 0 ? validationErrors : undefined;
    console.error("[AdminExpertSubscriptions] Failed to save plan:", err);
  } finally {
    planSubmissionInFlight.value = false;
  }
}

async function onOptionFormSubmit(values: BillingOptionFormValues) {
  if (!selectedPlanId.value) return;
  try {
    if (editingOption.value) {
      await updateOption({
        planId: selectedPlanId.value,
        billingOptionId: editingOption.value.id,
        data: formValuesToUpdateBillingOptionPayload(values),
      });
    } else {
      await createOption({
        planId: selectedPlanId.value,
        data: formValuesToCreateBillingOptionPayload(values),
      });
    }
    optionAsideOpen.value = false;
    editingOption.value = null;
  } catch (err) {
    console.error("[AdminExpertSubscriptions] Failed to save option:", err);
  }
}

async function onToggleOption(option: PlanBillingOption) {
  if (!selectedPlanId.value) return;
  try {
    await updateOption({
      planId: selectedPlanId.value,
      billingOptionId: option.id,
      data: { is_active: !option.is_active },
    });
  } catch (err) {
    console.error("[AdminExpertSubscriptions] Failed to toggle option:", err);
  }
}

// Delete confirmations
const planPendingDelete = ref<ExpertPlan | null>(null);
const optionPendingDelete = ref<PlanBillingOption | null>(null);
const planDeleteOpen = computed({
  get: () => planPendingDelete.value !== null,
  set: (open: boolean) => {
    if (!open) planPendingDelete.value = null;
  },
});
const optionDeleteOpen = computed({
  get: () => optionPendingDelete.value !== null,
  set: (open: boolean) => {
    if (!open) optionPendingDelete.value = null;
  },
});
const deletePlanCopy = computed(() => buildDeletePlanCopy(planPendingDelete.value));

async function confirmDeletePlan() {
  const target = planPendingDelete.value;
  if (!target) return;
  try {
    await deletePlan(target.id);
  } catch (err) {
    console.error("[AdminExpertSubscriptions] Failed to delete plan:", err);
  } finally {
    planPendingDelete.value = null;
  }
}

async function confirmDeleteOption() {
  const target = optionPendingDelete.value;
  if (!target || !selectedPlanId.value) return;
  try {
    await deleteOption({ planId: selectedPlanId.value, billingOptionId: target.id });
  } catch (err) {
    console.error("[AdminExpertSubscriptions] Failed to delete option:", err);
  } finally {
    optionPendingDelete.value = null;
  }
}
</script>

<template>
  <main class="flex-1 p-4 md:p-8 overflow-y-auto bg-secondary font-light w-full">
    <PageHeader
      title="Expert Subscriptions"
      description="Manage subscription plans and their recurring prices. Stripe synchronization happens automatically when pricing becomes sellable."
    >
      <template #action>
        <UiButton
          variant="primary"
          icon="plus-square"
          label="Create plan"
          @click="openCreatePlan"
        />
      </template>
    </PageHeader>

    <!-- Content on cards; only the heading sits on the page background -->
    <div class="flex flex-col lg:flex-row gap-6 items-start">
      <!-- Plan rail -->
      <aside class="w-full lg:w-72 xl:w-80 shrink-0 lg:sticky lg:top-6">
        <UiCard padding="sm">
          <PlanRail
            :plans="plans"
            :selected-plan-id="selectedPlanId"
            :is-loading="isLoading"
            @select="selectPlan"
          />
        </UiCard>
      </aside>

      <!-- Canvas -->
      <section class="flex-1 min-w-0">
        <UiCard padding="md">
        <!-- Plan detail -->
        <div v-if="selectedPlan" class="flex flex-col gap-6">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0 flex flex-col gap-1.5">
              <div class="flex items-center gap-2.5 min-w-0">
                <UiText as="h2" variant="h4" class="font-semibold text-se-dark truncate">
                  {{ selectedPlan.name }}
                </UiText>
                <UiChip
                  :text="selectedPlan.is_active ? 'Active' : 'Inactive'"
                  :variant="selectedPlan.is_active ? 'success' : 'neutral'"
                  size="xs"
                  :tooltip="selectedPlan.is_active
                    ? selectedPlan.expert_type
                      ? 'Offered to new experts of the selected type'
                      : 'Active, but not sellable until an expert type is assigned'
                    : 'Stays on existing subscriptions but not offered to new experts'"
                />
                <UiChip
                  :text="formatExpertPlanType(selectedPlan.expert_type)"
                  :variant="selectedPlan.expert_type ? 'outline' : 'warn'"
                  size="xs"
                  :tooltip="describeExpertPlanType(selectedPlan.expert_type)"
                />
                <UiChip
                  v-if="selectedPlan.is_founding"
                  text="Founding"
                  variant="highlight"
                  size="xs"
                  tooltip="Founding-member plan"
                />
              </div>
              <div class="flex items-center gap-2">
                <UiText as="span" variant="caption-normal" class="font-mono text-se-gray-500">{{ selectedPlan.slug }}</UiText>
                <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="·" />
                <UiText as="span" variant="caption-normal" class="text-se-gray-500">{{ subsLabel }}</UiText>
              </div>
              <UiText
                as="p"
                variant="caption-normal"
                class="text-se-gray-500"
                text="Edit the plan identity and availability below. Active Stripe product names stay synchronized automatically."
              />
            </div>

            <UiTooltip content="Delete plan">
              <UiIconButton
                icon="delete"
                variant="ghost"
                :aria-label="`Delete ${selectedPlan.name}`"
                @click="planPendingDelete = selectedPlan"
              />
            </UiTooltip>
          </div>

          <UiInfoBox
            v-if="!selectedPlan.expert_type"
            variant="warning"
            title="This legacy plan is not sellable"
          >
            <UiText
              as="p"
              variant="caption-normal"
              :text="selectedPlan.subscriptions_count > 0
                ? 'Its audience is locked because subscriptions already reference it. Existing subscriptions remain intact, but the plan cannot be used for new payment invitations.'
                : 'Choose Freelancer or Agency below before using this plan for payment invitations.'"
            />
          </UiInfoBox>

          <!-- One continuous identity section: context and editable fields belong together. -->
          <PlanForm
            :key="`${selectedPlan.id}-${planFormRevision}`"
            :initial-values="selectedPlanFormInitialValues"
            :is-editing="true"
            :is-submitting="isSavingPlan"
            :server-errors="updatePlanServerErrors"
            :expert-type-locked="selectedPlan.subscriptions_count > 0"
            inline
            :show-cancel="false"
            @submit="onUpdatePlanSubmit"
            @clear-server-errors="updatePlanServerErrors = undefined"
          />

          <UiDivider />

          <!-- Pricing -->
          <div class="flex flex-col gap-3">
            <div class="flex flex-col">
              <UiText as="h3" variant="h6" class="font-semibold text-se-dark" text="Pricing" />
              <UiText
                as="p"
                variant="caption-normal"
                class="text-se-gray-500"
                text="Recurring prices sold through payment invitations. Turn on Sellable when ready; Stripe synchronizes automatically."
              />
            </div>

            <div v-if="optionsLoading" class="py-10 text-center">
              <UiText as="p" variant="p-small" class="text-se-gray-500" text="Loading billing options..." />
            </div>

            <UiInfoBox
              v-else-if="options.length === 0"
              variant="info"
              title="No billing options yet"
            >
              <UiText
                as="p"
                variant="caption-normal"
                text="Add a recurring price to make this plan sellable through payment invitations."
              />
            </UiInfoBox>

            <PricingTable
              v-else
              :options="options"
              :is-toggling="isUpdatingOption"
              @edit="openOptionAside"
              @delete="(option) => (optionPendingDelete = option)"
              @toggle="onToggleOption"
            />

            <div class="flex justify-end pt-1">
              <UiButton
                variant="primary"
                icon="plus-square"
                label="Add option"
                @click="openOptionAside()"
              />
            </div>
          </div>
        </div>

        <!-- No selection -->
        <div v-else class="py-16 text-center">
          <UiText
            as="p"
            variant="p"
            class="text-se-gray-600"
            :text="isLoading ? 'Loading plans...' : 'Create a plan to start selling expert subscriptions.'"
          />
        </div>
        </UiCard>
      </section>
    </div>

    <!-- Small creation task; the detail canvas never loses its context. -->
    <UiAside v-model="planAsideOpen" position="right" :close-on-overlay-click="false">
      <div class="flex h-full max-h-full flex-col gap-6 overflow-y-auto px-4 py-6 md:p-8">
        <div class="flex items-start justify-between gap-4">
          <div class="flex flex-col gap-1">
            <UiText as="h2" variant="h3" class="font-semibold text-se-dark" text="Create plan" />
            <UiText
              as="p"
              variant="p-small"
              class="text-se-gray-600"
              text="Create the plan identity first, then add its recurring pricing from the workbench."
            />
          </div>
          <UiTooltip content="Close">
            <UiIconButton
              icon="close"
              variant="ghost"
              aria-label="Close create plan panel"
              :disabled="isSavingPlan"
              @click="closePlanAside"
            />
          </UiTooltip>
        </div>
        <PlanForm
          v-if="planAsideOpen"
          key="new-plan"
          :initial-values="defaultExpertPlanFormValues()"
          :is-editing="false"
          :is-submitting="isSavingPlan"
          :server-errors="createPlanServerErrors"
          @submit="onCreatePlanSubmit"
          @cancel="closePlanAside"
          @clear-server-errors="createPlanServerErrors = undefined"
        />
      </div>
    </UiAside>

    <!-- Billing options are focused edits, kept beside the selected plan. -->
    <UiAside
      v-model="optionAsideOpen"
      position="right"
      :close-on-overlay-click="false"
      @close="editingOption = null"
    >
      <div class="flex h-full max-h-full flex-col gap-6 overflow-y-auto px-4 py-6 md:p-8">
        <div class="flex items-start justify-between gap-4">
          <div class="flex flex-col gap-1 min-w-0">
            <UiText
              as="h2"
              variant="h3"
              class="font-semibold text-se-dark"
              :text="editingOption ? `Edit ${editingOption.label}` : 'Add billing option'"
            />
            <UiText
              as="p"
              variant="p-small"
              class="text-se-gray-600"
              :text="selectedPlan
                ? `Pricing for ${selectedPlan.name}. Active pricing synchronizes with Stripe when saved.`
                : ''"
            />
          </div>
          <UiTooltip content="Close">
            <UiIconButton
              icon="close"
              variant="ghost"
              aria-label="Close billing option panel"
              :disabled="isSavingOption"
              @click="closeOptionAside"
            />
          </UiTooltip>
        </div>
        <BillingOptionForm
          v-if="optionAsideOpen && selectedPlan"
          :key="editingOption?.id ?? 'new-option'"
          :plan-name="selectedPlan.name"
          :initial-values="optionFormInitialValues"
          :is-editing="editingOption !== null"
          :is-submitting="isSavingOption"
          @submit="onOptionFormSubmit"
          @cancel="closeOptionAside"
        />
      </div>
    </UiAside>

    <!-- Delete confirmations -->
    <UiDialog
      v-model="planDeleteOpen"
      v-model:loading="isDeletingPlan"
      :title="deletePlanCopy.title"
      :text="deletePlanCopy.text"
      :primary-action-label="deletePlanCopy.primaryActionLabel"
      :secondary-action-label="deletePlanCopy.secondaryActionLabel"
      @primary-action="confirmDeletePlan"
      @secondary-action="planDeleteOpen = false"
    />
    <UiDialog
      v-model="optionDeleteOpen"
      v-model:loading="isDeletingOption"
      title="Delete billing option?"
      :text="optionPendingDelete && selectedPlan
        ? `Remove “${optionPendingDelete.label}” (${formatOptionAmount(optionPendingDelete)}) from ${selectedPlan.name}? Deletion is refused while invitations, subscriptions, or invoices reference it.`
        : ''"
      primary-action-label="Delete option"
      secondary-action-label="Keep option"
      @primary-action="confirmDeleteOption"
      @secondary-action="optionDeleteOpen = false"
    />
  </main>
</template>
